/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderFormSchemaFactory, FormRootSchema, FormState, FormStateSchema } from '@gh/builder-shared';
import { DateTimeTypes, MaskedTypes, NumberTypes } from '@gh/common-shared';
import { getControl, setControlValue } from '@gh/core-ui';
import { yearsSince } from '@gh/core-util';
import { distinctUntilChanged$, filter$, map$ } from '@gh/rx/operators';
import { calculateHourlyRate } from '../../contact-details-util';
import { ContactDetailsEmployeeCommon, isSalaryBased } from '../../resources/model/contact-details-employee.model';

@Injectable()
export class ContactDetailsEmployeeCommonFormState extends FormState<ContactDetailsEmployeeCommon> {
  plain: {
    age?: number;
  } = {};

  private isInit = true;

  constructor(schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  build(common: ContactDetailsEmployeeCommon): void {
    this.initSchema(common);

    if (this.isInit) {
      this.calculatable(getControl(this.group, 'salary')).pipe(
        filter$(isSalaryBased),
        map$(calculateHourlyRate),
        distinctUntilChanged$())
        .subscribe((rate) => {
          setControlValue(this.group, 'salary.salaryBasisHourlyRate', rate);
        });

      this.calculatable(getControl(this.group, 'personal.dateOfBirth')).pipe(
        map$((birthday: Date) => birthday ? yearsSince(birthday) : 0))
        .subscribe((age) => {
          this.plain.age = age >= 0 ? age : void 0;
        });

      this.isInit = false;
    }
  }

  protected buildSchema(value: ContactDetailsEmployeeCommon, { root, group }: FormStateSchema): FormRootSchema {
    return root({
      children: {
        personal: group({
          children: {
            nameToPrintOnChecks: void 0,
            taxFileNumber: { type: MaskedTypes.TaxFileNumber },
            gender: { required: true },
            dateOfBirth: { type: DateTimeTypes.ShortDate },
            startDate: { type: DateTimeTypes.ShortDate },
            terminationDate: { type: DateTimeTypes.ShortDate },
          },
        }),
        salary: group({
          children: {
            payrollCycle: { required: true },
            hoursInPayPeriod: { required: true, type: NumberTypes.HoursNotZero },
            payBasis: { required: true },
            salary: { type: NumberTypes.Price },
            salaryBasisHourlyRate: {
              enabled: false,
              type: NumberTypes.Price,
            },
            hourlyBasisHourlyRate: { type: NumberTypes.Price },
            commissionRate: { type: NumberTypes.Commission },
          },
        }),
      },
    });
  }
}
